import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { LogOut, Plus, Building2, Trash2, Users, Power, ChevronRight, Calendar, CheckCircle2, Mail, Send } from "lucide-react";
import LeadsTab from "../components/LeadsTab";
import SmtpTab from "../components/SmtpTab";

const SuperAdmin = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [companyUsers, setCompanyUsers] = useState({}); // id -> users[]
  const [openCreate, setOpenCreate] = useState(false);
  const [newCompany, setNewCompany] = useState({ name: "", owner_email: "", owner_password: "", owner_name: "" });
  const [openCreateUser, setOpenCreateUser] = useState(null); // company_id
  const [newUser, setNewUser] = useState({ email: "", password: "", name: "", role: "operator" });
  const [activationMeta, setActivationMeta] = useState({}); // company_id -> {reason, payment_method}
  const [historyByCompany, setHistoryByCompany] = useState({}); // company_id -> rows[]

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/super-admin/companies");
      setCompanies(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchUsersFor = async (companyId) => {
    try {
      const { data } = await api.get(`/super-admin/companies/${companyId}/users`);
      setCompanyUsers((prev) => ({ ...prev, [companyId]: data }));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleExpand = (id) => {
    if (expanded === id) {
      setExpanded(null);
      return;
    }
    setExpanded(id);
    if (!companyUsers[id]) fetchUsersFor(id);
    if (!historyByCompany[id]) loadHistory(id);
  };

  const createCompany = async (e) => {
    e.preventDefault();
    try {
      await api.post("/super-admin/companies", newCompany);
      toast.success(`Empresa "${newCompany.name}" criada`);
      setOpenCreate(false);
      setNewCompany({ name: "", owner_email: "", owner_password: "", owner_name: "" });
      fetchCompanies();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleCompanyActive = async (c) => {
    try {
      await api.patch(`/super-admin/companies/${c.id}`, { active: !c.active });
      toast.success(`${c.name} ${!c.active ? "ativada" : "desativada"}`);
      fetchCompanies();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteCompany = async (c) => {
    try {
      await api.delete(`/super-admin/companies/${c.id}`);
      toast.success("Empresa removida");
      fetchCompanies();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const createUserForCompany = async (e) => {
    e.preventDefault();
    if (!openCreateUser) return;
    try {
      await api.post(`/super-admin/companies/${openCreateUser}/users`, newUser);
      toast.success(`Usuário "${newUser.name}" criado`);
      setOpenCreateUser(null);
      setNewUser({ email: "", password: "", name: "", role: "operator" });
      fetchUsersFor(openCreateUser);
      fetchCompanies();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteUser = async (userId, companyId) => {
    try {
      await api.delete(`/users/${userId}`);
      toast.success("Usuário removido");
      fetchUsersFor(companyId);
      fetchCompanies();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const activateCompany = async (c, months, plan = null) => {
    const meta = activationMeta[c.id] || {};
    try {
      const { data } = await api.post(`/super-admin/companies/${c.id}/activate`, {
        months,
        ...(plan ? { plan } : {}),
        ...(meta.reason ? { reason: meta.reason } : {}),
        ...(meta.payment_method ? { payment_method: meta.payment_method } : {}),
      });
      const until = new Date(data.subscription.paid_until).toLocaleDateString("pt-BR");
      toast.success(`${c.name}: ${data.plan_label} ativo até ${until}`);
      setActivationMeta((p) => ({ ...p, [c.id]: { reason: "", payment_method: "" } }));
      fetchCompanies();
      if (historyByCompany[c.id] !== undefined) loadHistory(c.id);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const loadHistory = async (companyId) => {
    try {
      const { data } = await api.get(`/super-admin/companies/${companyId}/activations`);
      setHistoryByCompany((p) => ({ ...p, [companyId]: data }));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deactivateCompany = async (c) => {
    if (!window.confirm(`Forçar ${c.name} para modo somente-leitura agora?`)) return;
    try {
      await api.post(`/super-admin/companies/${c.id}/deactivate`);
      toast.success(`${c.name} foi posta em modo somente-leitura`);
      fetchCompanies();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border sticky top-0 bg-background z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 bg-foreground" />
            <span className="font-heading font-bold tracking-tighter text-lg">Plataforma · Super Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs uppercase tracking-[0.25em] text-foreground/60 hidden md:inline">
              {user?.email}
            </span>
            <Button variant="outline" size="sm" className="rounded-none" onClick={() => navigate("/")} data-testid="sa-home-btn">
              Início
            </Button>
            <Button variant="outline" size="sm" className="rounded-none" onClick={logout} data-testid="sa-logout-btn">
              <LogOut className="w-4 h-4 mr-2" /> Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        <Tabs defaultValue="companies" className="w-full">
          <TabsList className="rounded-none border border-border bg-background p-0 h-auto mb-10 inline-flex" data-testid="sa-tabs">
            <TabsTrigger
              value="companies"
              className="rounded-none px-6 py-3 text-xs uppercase tracking-[0.2em] data-[state=active]:bg-foreground data-[state=active]:text-background"
              data-testid="sa-tab-companies"
            >
              <Building2 className="w-3 h-3 mr-2" /> Empresas
            </TabsTrigger>
            <TabsTrigger
              value="leads"
              className="rounded-none px-6 py-3 text-xs uppercase tracking-[0.2em] data-[state=active]:bg-foreground data-[state=active]:text-background border-l border-border"
              data-testid="sa-tab-leads"
            >
              <Mail className="w-3 h-3 mr-2" /> Leads
            </TabsTrigger>
            <TabsTrigger
              value="smtp"
              className="rounded-none px-6 py-3 text-xs uppercase tracking-[0.2em] data-[state=active]:bg-foreground data-[state=active]:text-background border-l border-border"
              data-testid="sa-tab-smtp"
            >
              <Send className="w-3 h-3 mr-2" /> SMTP
            </TabsTrigger>
          </TabsList>

          <TabsContent value="companies" className="mt-0">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-foreground/60 mb-3">Empresas Cadastradas</div>
            <h1 className="font-heading text-4xl md:text-5xl font-black tracking-tighter">
              {companies.length} {companies.length === 1 ? "Empresa" : "Empresas"}
            </h1>
          </div>
          <Dialog open={openCreate} onOpenChange={setOpenCreate}>
            <DialogTrigger asChild>
              <Button className="rounded-none h-12 px-6 text-sm uppercase tracking-[0.2em]" data-testid="sa-new-company-btn">
                <Plus className="w-4 h-4 mr-2" /> Nova Empresa
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none max-w-lg" data-testid="sa-new-company-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl font-black tracking-tighter">Nova Empresa</DialogTitle>
              </DialogHeader>
              <form onSubmit={createCompany} className="space-y-4 py-4">
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em]">Nome da Empresa</Label>
                  <Input
                    value={newCompany.name}
                    onChange={(e) => setNewCompany((p) => ({ ...p, name: e.target.value }))}
                    placeholder="Ex: Clínica Saúde+"
                    className="mt-2 h-11 rounded-none"
                    data-testid="sa-company-name-input"
                    required
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em]">Nome do Dono</Label>
                  <Input
                    value={newCompany.owner_name}
                    onChange={(e) => setNewCompany((p) => ({ ...p, owner_name: e.target.value }))}
                    placeholder="Ex: Dr. João Silva"
                    className="mt-2 h-11 rounded-none"
                    data-testid="sa-owner-name-input"
                    required
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em]">Email do Dono</Label>
                  <Input
                    type="email"
                    value={newCompany.owner_email}
                    onChange={(e) => setNewCompany((p) => ({ ...p, owner_email: e.target.value }))}
                    placeholder="owner@empresa.com"
                    className="mt-2 h-11 rounded-none"
                    data-testid="sa-owner-email-input"
                    required
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em]">Senha Inicial</Label>
                  <Input
                    type="text"
                    value={newCompany.owner_password}
                    onChange={(e) => setNewCompany((p) => ({ ...p, owner_password: e.target.value }))}
                    placeholder="Mínimo 6 caracteres"
                    className="mt-2 h-11 rounded-none font-mono"
                    data-testid="sa-owner-password-input"
                    minLength={6}
                    required
                  />
                </div>
                <DialogFooter>
                  <Button type="submit" className="rounded-none uppercase tracking-[0.2em] w-full h-12" data-testid="sa-create-company-submit">
                    Criar Empresa
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-foreground/50">Carregando…</div>
        ) : companies.length === 0 ? (
          <div className="border border-dashed border-border p-12 text-center">
            <Building2 className="w-10 h-10 mx-auto mb-4 opacity-40" />
            <div className="text-sm uppercase tracking-[0.2em] text-foreground/60">Nenhuma empresa cadastrada ainda</div>
          </div>
        ) : (
          <div className="border-t border-border">
            {companies.map((c) => (
              <div key={c.id} className="border-b border-border" data-testid={`sa-company-row-${c.id}`}>
                <button
                  onClick={() => toggleExpand(c.id)}
                  className="w-full flex items-center justify-between gap-4 px-2 md:px-6 py-6 hover:bg-muted/40 transition-colors text-left"
                  data-testid={`sa-company-toggle-${c.id}`}
                >
                  <div className="flex items-center gap-4">
                    <ChevronRight className={`w-5 h-5 transition-transform ${expanded === c.id ? "rotate-90" : ""}`} />
                    <div>
                      <div className="font-heading text-2xl md:text-3xl font-black tracking-tighter">{c.name}</div>
                      <div className="text-xs text-foreground/60 mt-1 flex items-center gap-3 flex-wrap">
                        <span className="font-mono">{c.slug}</span>
                        <span>·</span>
                        <span>{c.user_count} {c.user_count === 1 ? "usuário" : "usuários"}</span>
                        <span>·</span>
                        <span className={c.active ? "text-foreground" : "text-destructive"}>
                          {c.active ? "Ativa" : "Inativa"}
                        </span>
                        {c.subscription && (
                          <>
                            <span>·</span>
                            <span
                              className={`uppercase tracking-wider font-semibold ${
                                c.subscription.active ? "text-foreground" : "text-destructive"
                              }`}
                              data-testid={`sa-sub-plan-${c.id}`}
                            >
                              {c.subscription.plan} · {c.subscription.active ? "ATIVA" : "EXPIRADA"}
                            </span>
                            {c.subscription.paid_until && (
                              <>
                                <span>·</span>
                                <span className="font-mono" data-testid={`sa-sub-until-${c.id}`}>
                                  até {new Date(c.subscription.paid_until).toLocaleDateString("pt-BR")}
                                </span>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="hidden md:block text-xs uppercase tracking-[0.2em] text-foreground/40">
                    {new Date(c.created_at).toLocaleDateString()}
                  </div>
                </button>

                {expanded === c.id && (
                  <div className="px-2 md:px-6 pb-8 bg-muted/20">
                    {/* Manual activation panel */}
                    <div className="border border-foreground bg-background p-6 mb-6" data-testid={`sa-activation-panel-${c.id}`}>
                      <div className="flex items-center gap-3 mb-2">
                        <Calendar className="w-4 h-4" />
                        <span className="text-xs uppercase tracking-[0.25em] text-foreground/60">
                          Ativação Manual (sem Stripe)
                        </span>
                      </div>
                      <p className="text-xs text-foreground/60 mb-5">
                        Use para clientes que pagaram via PIX, boleto, transferência ou para estender trials. Cada
                        clique <strong>soma</strong> o período ao tempo restante atual.
                      </p>

                      {/* Optional audit fields */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
                        <div>
                          <Label className="text-[10px] uppercase tracking-[0.2em] text-foreground/60">
                            Motivo / Observação (opcional)
                          </Label>
                          <Input
                            value={activationMeta[c.id]?.reason || ""}
                            onChange={(e) =>
                              setActivationMeta((p) => ({
                                ...p,
                                [c.id]: { ...(p[c.id] || {}), reason: e.target.value },
                              }))
                            }
                            placeholder="Ex: Pagamento confirmado por PIX em 22/05"
                            className="mt-1 h-10 rounded-none text-sm"
                            data-testid={`sa-activate-reason-${c.id}`}
                          />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-[0.2em] text-foreground/60">
                            Método de Pagamento (opcional)
                          </Label>
                          <select
                            value={activationMeta[c.id]?.payment_method || ""}
                            onChange={(e) =>
                              setActivationMeta((p) => ({
                                ...p,
                                [c.id]: { ...(p[c.id] || {}), payment_method: e.target.value },
                              }))
                            }
                            className="mt-1 h-10 w-full rounded-none border border-input bg-background px-3 text-sm"
                            data-testid={`sa-activate-method-${c.id}`}
                          >
                            <option value="">— selecione —</option>
                            <option value="pix">PIX</option>
                            <option value="boleto">Boleto</option>
                            <option value="transferencia">Transferência</option>
                            <option value="cartao">Cartão</option>
                            <option value="cortesia">Cortesia</option>
                            <option value="trial_extension">Extensão de Trial</option>
                            <option value="outro">Outro</option>
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                        {[
                          [1, "1 mês"],
                          [3, "3 meses"],
                          [6, "6 meses"],
                          [12, "1 ano"],
                        ].map(([m, label]) => (
                          <Button
                            key={m}
                            onClick={() => activateCompany(c, m)}
                            className="rounded-none h-auto py-4 flex flex-col items-start gap-1"
                            data-testid={`sa-activate-${c.id}-${m}m`}
                          >
                            <span className="text-[10px] uppercase tracking-[0.2em] opacity-70">Adicionar</span>
                            <span className="font-heading text-2xl font-black tracking-tighter">{label}</span>
                          </Button>
                        ))}
                      </div>

                      <div className="border-t border-border pt-4">
                        <div className="text-xs uppercase tracking-[0.2em] text-foreground/60 mb-3">
                          Trocar Plano + Período
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                          {["basico", "pro", "enterprise"].map((p) => (
                            <div key={p} className="border border-border p-3 flex flex-col gap-2">
                              <span className="text-xs uppercase tracking-[0.2em] font-semibold capitalize">{p}</span>
                              <div className="flex flex-wrap gap-1">
                                {[1, 3, 6, 12].map((m) => (
                                  <Button
                                    key={m}
                                    onClick={() => activateCompany(c, m, p)}
                                    variant="outline"
                                    size="sm"
                                    className="rounded-none text-xs h-7 px-2"
                                    data-testid={`sa-activate-${c.id}-${p}-${m}m`}
                                  >
                                    +{m}m
                                  </Button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {c.subscription?.active && (
                        <div className="mt-4 flex items-center gap-2 text-xs text-foreground/70">
                          <CheckCircle2 className="w-4 h-4" />
                          Atualmente ativa até <span className="font-mono">{new Date(c.subscription.paid_until).toLocaleDateString("pt-BR")}</span>
                        </div>
                      )}
                    </div>

                    {/* Activation history */}
                    {historyByCompany[c.id] && historyByCompany[c.id].length > 0 && (
                      <div className="border border-border bg-background mb-6" data-testid={`sa-history-${c.id}`}>
                        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-foreground/60">
                            <Calendar className="w-4 h-4" /> Histórico de Ativações
                          </div>
                          <span className="text-xs text-foreground/40">{historyByCompany[c.id].length} registros</span>
                        </div>
                        <div className="max-h-72 overflow-y-auto">
                          {historyByCompany[c.id].map((h) => (
                            <div
                              key={h.id}
                              className="px-4 py-3 border-b border-border last:border-b-0 grid grid-cols-1 md:grid-cols-[140px_1fr_140px] gap-3 text-sm"
                              data-testid={`sa-history-row-${h.id}`}
                            >
                              <div className="text-xs text-foreground/60 font-mono">
                                {new Date(h.activated_at).toLocaleString("pt-BR")}
                              </div>
                              <div>
                                <div className="font-medium">
                                  +{h.months} {h.months === 1 ? "mês" : "meses"} · plano <span className="uppercase">{h.plan}</span>
                                </div>
                                <div className="text-xs text-foreground/60 mt-0.5">
                                  {h.payment_method && (
                                    <span className="uppercase tracking-wider mr-2 border border-border px-1.5 py-0.5">
                                      {h.payment_method}
                                    </span>
                                  )}
                                  {h.reason || <span className="italic">sem observação</span>}
                                </div>
                              </div>
                              <div className="text-xs text-foreground/40 text-right md:text-left">
                                até {new Date(h.new_paid_until).toLocaleDateString("pt-BR")}
                                {h.activated_by_name && (
                                  <div className="text-[10px] uppercase tracking-wider">por {h.activated_by_name}</div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex flex-wrap gap-2 mb-6">
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-none"
                        onClick={() => toggleCompanyActive(c)}
                        data-testid={`sa-toggle-active-${c.id}`}
                      >
                        <Power className="w-4 h-4 mr-2" /> {c.active ? "Desativar" : "Ativar"}
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-none text-destructive hover:text-destructive"
                        onClick={() => deactivateCompany(c)}
                        data-testid={`sa-readonly-${c.id}`}
                        disabled={!c.subscription?.active}
                      >
                        Forçar somente-leitura
                      </Button>

                      <Dialog
                        open={openCreateUser === c.id}
                        onOpenChange={(o) => setOpenCreateUser(o ? c.id : null)}
                      >
                        <DialogTrigger asChild>
                          <Button size="sm" className="rounded-none" data-testid={`sa-new-user-btn-${c.id}`}>
                            <Plus className="w-4 h-4 mr-2" /> Novo Usuário
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="rounded-none max-w-lg">
                          <DialogHeader>
                            <DialogTitle className="font-heading text-2xl font-black tracking-tighter">
                              Novo usuário em {c.name}
                            </DialogTitle>
                          </DialogHeader>
                          <form onSubmit={createUserForCompany} className="space-y-4 py-4">
                            <div>
                              <Label className="text-xs uppercase tracking-[0.2em]">Nome</Label>
                              <Input
                                value={newUser.name}
                                onChange={(e) => setNewUser((p) => ({ ...p, name: e.target.value }))}
                                className="mt-2 h-11 rounded-none"
                                data-testid="sa-user-name-input"
                                required
                              />
                            </div>
                            <div>
                              <Label className="text-xs uppercase tracking-[0.2em]">Email</Label>
                              <Input
                                type="email"
                                value={newUser.email}
                                onChange={(e) => setNewUser((p) => ({ ...p, email: e.target.value }))}
                                className="mt-2 h-11 rounded-none"
                                data-testid="sa-user-email-input"
                                required
                              />
                            </div>
                            <div>
                              <Label className="text-xs uppercase tracking-[0.2em]">Senha</Label>
                              <Input
                                type="text"
                                value={newUser.password}
                                onChange={(e) => setNewUser((p) => ({ ...p, password: e.target.value }))}
                                className="mt-2 h-11 rounded-none font-mono"
                                minLength={6}
                                data-testid="sa-user-password-input"
                                required
                              />
                            </div>
                            <div>
                              <Label className="text-xs uppercase tracking-[0.2em]">Função</Label>
                              <Select
                                value={newUser.role}
                                onValueChange={(v) => setNewUser((p) => ({ ...p, role: v }))}
                              >
                                <SelectTrigger className="mt-2 h-11 rounded-none" data-testid="sa-user-role-select">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="owner" data-testid="sa-role-owner">Dono (acesso total)</SelectItem>
                                  <SelectItem value="operator" data-testid="sa-role-operator">Operador (apenas chamadas)</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <DialogFooter>
                              <Button type="submit" className="rounded-none uppercase tracking-[0.2em] w-full h-12">
                                Criar Usuário
                              </Button>
                            </DialogFooter>
                          </form>
                        </DialogContent>
                      </Dialog>

                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="destructive" size="sm" className="rounded-none" data-testid={`sa-delete-company-${c.id}`}>
                            <Trash2 className="w-4 h-4 mr-2" /> Excluir Empresa
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="rounded-none">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Excluir {c.name}?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Esta ação remove a empresa, todos os usuários e todo o histórico associado. Não pode ser desfeita.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="rounded-none">Cancelar</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => deleteCompany(c)}
                              className="rounded-none bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              data-testid={`sa-confirm-delete-company-${c.id}`}
                            >
                              Excluir
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>

                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-foreground/60 mb-4">
                      <Users className="w-4 h-4" /> Usuários
                    </div>
                    <div className="border border-border bg-background">
                      {(companyUsers[c.id] || []).map((u) => (
                        <div
                          key={u.id}
                          className="flex items-center justify-between gap-4 px-4 py-3 border-b border-border last:border-b-0"
                          data-testid={`sa-user-row-${u.id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{u.name}</div>
                            <div className="text-xs text-foreground/60 truncate">
                              {u.email} · <span className="uppercase tracking-wider">{u.role}</span>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="rounded-none"
                            onClick={() => deleteUser(u.id, c.id)}
                            data-testid={`sa-delete-user-${u.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      {(companyUsers[c.id] || []).length === 0 && (
                        <div className="px-4 py-3 text-sm text-foreground/40">Carregando…</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
          </TabsContent>

          <TabsContent value="leads" className="mt-0">
            <LeadsTab />
          </TabsContent>

          <TabsContent value="smtp" className="mt-0">
            <SmtpTab />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default SuperAdmin;
